import React, { useState, useEffect, useCallback, useRef } from 'react';
import debounce from 'lodash/debounce';
import CardDisplay from './CardDisplay';
import CardForm from './CardForm';
import './App.css';

// Browser-compatible random token generator
const generateToken = () => {
  const array = new Uint8Array(32);
  window.crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
};

// Constants
const MAX_SUBMIT_ATTEMPTS = 5;
const SUBMIT_COOLDOWN_MS = 60000;
const API_TIMEOUT_MS = 10000;
const LACAISSE_TOKEN = process.env.REACT_APP_LACAISSE_TOKEN;
const errorCodeMap = {
  '101': 'Numéro de carte invalide',
  '203': 'Transaction refusée par l\'émetteur',
  '301': 'Signature invalide',
  '400': 'Requête invalide',
  '4004': 'Email invalide ou manquant',
  '500': 'Erreur serveur',
};

// French error messages for better UX
const getFrenchErrorMessage = (error) => {
  const errorLower = error.toLowerCase();
  
  if (errorLower.includes('card number') || errorLower.includes('checksum') || errorLower.includes('carte')) {
    return 'Les informations de votre carte bancaire sont incorrectes. Veuillez vérifier et réessayer.';
  }
  if (errorLower.includes('expir')) {
    return 'La date d\'expiration de votre carte est invalide ou dépassée.';
  }
  if (errorLower.includes('cvv') || errorLower.includes('ccv')) {
    return 'Le code CVV de votre carte est incorrect.';
  }
  if (errorLower.includes('fill all') || errorLower.includes('required')) {
    return 'Veuillez remplir tous les champs obligatoires.';
  }
  if (errorLower.includes('network') || errorLower.includes('connection')) {
    return 'Erreur de connexion. Veuillez vérifier votre connexion internet et réessayer.';
  }
  if (errorLower.includes('declined') || errorLower.includes('refusé')) {
    return 'Le paiement a été refusé. Veuillez contacter votre banque ou utiliser une autre carte.';
  }
  if (errorLower.includes('session')) {
    return 'Votre session a expiré. Veuillez recommencer le processus de paiement.';
  }
  if (errorLower.includes('too many')) {
    return 'Trop de tentatives. Veuillez patienter quelques instants avant de réessayer.';
  }
  
  return 'Le paiement n\'a pas pu être effectué. Veuillez vérifier vos informations et réessayer.';
};

const App = () => {
  const [state, setState] = useState({
    name: '',
    email: '',
    input: '',
    expiry: '',
    ccv: '',
    errormsg: '',
    cardType: null,
    isExpiryFocused: false,
    submitAttempts: 0,
    lastSubmitTime: 0,
    orderId: '',
    amount: '',
    loading: false,
    isAppReady: false,
    config: null,
    sessionId: '',
    sessionToken: null,
    sessionVerified: false,
    sessionTouched: false,
    saveCard: false,
    showErrorModal: false,
    savedCards: [],
    selectedCard: null,
    showCvvModal: false,
    savedCardCvv: '',
    useNewCard: false,
    showDeleteModal: false,
    cardToDelete: null,
  });

  const hasInitialized = useRef(false);

  // Simple logging
  const customLog = (...args) => {
    console.log(...args);
  };
  const customError = (...args) => {
    console.error(...args);
  };

  // Helper Functions
  const sanitizeInput = useCallback((value) => value.replace(/[<>:"/\\|?*]/g, ''), []);

  // Check if phoneClient exists in any cardName
  const hasPhoneClientInCards = useCallback(() => {
    if (!state.config?.customer?.phoneClient || !state.savedCards.length) return false;
    
    const phoneClient = state.config.customer.phoneClient;
    const cleanPhoneClient = phoneClient.replace(/[^\d]/g, ''); // Remove all non-digits
    
    customLog('Checking phoneClient matching:', {
      originalPhoneClient: phoneClient,
      cleanPhone: cleanPhoneClient,
      configCustomerPhone: state.config?.customer?.phone,
      configCustomerPhoneClient: state.config?.customer?.phoneClient,
      savedCards: state.savedCards.map(card => ({
        cardName: card.cardName,
        customerBankCardId: card.customerBankCardId,
        cardholderFirstname: card.cardholderFirstname,
        cardholderLastname: card.cardholderLastname
      }))
    });
    
    const hasMatch = state.savedCards.some(card => {
      if (!card.cardName) return false;
      
      const cleanCardName = card.cardName.replace(/[^\d]/g, ''); // Remove all non-digits
      const isMatch = cleanCardName === cleanPhoneClient || 
                     card.cardName.toLowerCase().includes(phoneClient.toLowerCase()) ||
                     cleanCardName.includes(cleanPhoneClient);
      
      customLog('Card match check:', {
        cardName: card.cardName,
        cleanCardName: cleanCardName,
        phoneClient: phoneClient,
        cleanPhoneClient: cleanPhoneClient,
        isMatch: isMatch
      });
      
      return isMatch;
    });
    
    customLog('hasPhoneClientInCards result:', hasMatch);
    return hasMatch;
  }, [state.config?.customer?.phoneClient, state.savedCards]);

  const luhnCheck = useCallback((cardNumber) => {
    let sum = 0;
    let isEven = false;
    for (let i = cardNumber.length - 1; i >= 0; i--) {
      let digit = parseInt(cardNumber.charAt(i), 10);
  if (isEven) {
        digit *= 2;
        if (digit > 9) digit -= 9;
      }
      sum += digit;
      isEven = !isEven;
    }
    return sum % 10 === 0;
  }, []);

  const validateCardNumber = useCallback(
    (number) => {
      const cleaned = number.replace(/\s/g, '');
      if (!/^\d{13,16}$/.test(cleaned)) {
        return { valid: false, error: 'Card number must be 13 to 16 digits' };
      }
      if (!luhnCheck(cleaned)) {
        return { valid: false, error: 'Invalid card number (checksum failed)' };
      }
      return { valid: true, error: '' };
    },
    [luhnCheck]
  );

  const detectCardType = useCallback((number) => {
    const cleanedNumber = number.replace(/\s/g, '');
    if (/^4/.test(cleanedNumber)) return 'visa';
    if (/^5[1-5]/.test(cleanedNumber)) return 'mastercard';
    if (/^3[47]/.test(cleanedNumber)) return 'amex';
    return null;
  }, []);



  // Session Verification
  const verifySession = useCallback(
    async (id) => {
      const verifyUrl = 'https://sessiondev.lacaissepay.ma/verifySession';
      // Prod URL: const verifyUrl = 'https://sessiondev.lacaissepay.ma/verifySession';
      const startTime = performance.now();
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), API_TIMEOUT_MS);

      try {
        const response = await fetch(verifyUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({ sessionId: id }),
          signal: controller.signal,
        });

        clearTimeout(timeout);

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Session verification failed: ${response.status} - ${errorText}`);
        }

        const { status, token } = await response.json();
        if (status !== 'verified') throw new Error('Session not verified');

        customLog(`Session ${id} verified successfully with token ${token}`);
        return token;
      } catch (error) {
        customError('Session verification error:', error.message);
        setState((prev) => ({
          ...prev,
          errormsg: 'Unable to verify session. Please try again.',
          isAppReady: false,
          loading: false,
        }));
        return null;
      }
    },
    []
  );

  // Fetch Payment Data
  const fetchPaymentData = useCallback(
    async (sessionId, sessionToken) => {
      const paymentUrl = 'https://sessiondev.lacaissepay.ma/getPaymentData';
      // Prod URL: const paymentUrl = 'https://sessiondev.lacaissepay.ma/getPaymentData';
      const startTime = performance.now();
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), API_TIMEOUT_MS);

      try {
        const response = await fetch(paymentUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            'X-Session-Token': sessionToken,
          },
          body: JSON.stringify({ sessionId }),
          signal: controller.signal,
        });

        clearTimeout(timeout);

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to fetch payment data: ${response.status} - ${errorText}`);
        }

        const data = await response.json();
        if (!data.amount || !data.orderId) {
          throw new Error('Invalid payment data');
        }
        customLog('Payment data fetched successfully:', data);
        return data;
      } catch (error) {
        customError('Fetch payment data error:', error.message);
        setState((prev) => ({
          ...prev,
          errormsg: 'Failed to load payment details.',
          isAppReady: false,
          loading: false,
        }));
        throw error;
      }
    },
    []
  );

  // Fetch Tokenized Cards
  const fetchTokenizedCards = useCallback(
    async (phoneNumber) => {
      if (!phoneNumber) {
        customLog('No phone number provided, skipping tokenized cards fetch');
        return [];
      }

      const apiUrl = `https://baasapidev.lacaissepay.ma/api/customers/tokenized/cards?phoneNumber=${encodeURIComponent(phoneNumber)}`;
      const lacaisseToken = '53b3329da8c333a0c0aaaa5ebd2b9776cfc87226800cfcbc109aac4d4a380b06';

      try {
        customLog('Fetching tokenized cards for phone:', phoneNumber);
        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'x-lacaisse-token': lacaisseToken,
          },
        });

        if (!response.ok) {
          customError('Failed to fetch tokenized cards:', response.status);
          return [];
        }

        const result = await response.json();
        customLog('Tokenized cards response:', result);

        if (result.status === 'success' && result.data?.data?.collection) {
          const cards = result.data.data.collection;
          customLog('Found saved cards:', cards.length);
          return cards;
        }

        return [];
      } catch (error) {
        customError('Error fetching tokenized cards:', error.message);
        return [];
      }
    },
    []
  );


  // Event Handlers
  const handleCardNumberChange = useCallback(
    (value) => {
      const rawValue = sanitizeInput(value).replace(/\s/g, '');
      if (!/^\d*$/.test(rawValue)) return;
      // Limit to 16 digits maximum
      if (rawValue.length > 16) return;
      const formattedValue = rawValue.replace(/(.{4})/g, '$1 ').trim();
      const cardType = detectCardType(rawValue);
      // Remove real-time validation - only validate on submit
      setState((prev) => ({ ...prev, input: formattedValue, cardType, sessionTouched: true }));
    },
    [sanitizeInput, detectCardType]
  );

  const handleExpiryChange = useCallback(
    (value) => {
      let formattedValue = '';
      const cleanedValue = value.replace(/[^0-9/]/g, '');
      let month = '';
      let year = '';

      if (cleanedValue.length > 0) {
        const parts = cleanedValue.split('/');
        month = parts[0].slice(0, 2);
        year = parts.length > 1 ? parts[1].slice(0, 2) : '';

        if (month.length === 2) {
          const monthNum = parseInt(month, 10);
          if (monthNum < 1 || monthNum > 12) {
            // Don't show error, just prevent invalid month
            formattedValue = month.slice(0, 1) + (year ? '/' + year : '');
          } else {
            formattedValue = month + '/' + (year || '');
          }
        } else {
          formattedValue = month + (year ? '/' + year : '');
        }

        if (formattedValue.length > 5) formattedValue = formattedValue.slice(0, 5);
      }

      // Remove real-time validation - only validate on submit
      setState((prev) => ({ ...prev, expiry: formattedValue, sessionTouched: true }));
    },
    []
  );

  const handleCcvChange = useCallback(
    (value) => {
      const sanitizedValue = sanitizeInput(value).replace(/\D/g, '').slice(0, 4);
      setState((prev) => ({ ...prev, ccv: sanitizedValue, sessionTouched: true }));
    },
    [sanitizeInput]
  );

  const getName = useCallback(
    (e) => {
      const value = sanitizeInput(e.target.value);
      setState((prev) => ({ ...prev, name: value, sessionTouched: true }));
    },
    [sanitizeInput]
  );

  const handleExpiryFocus = useCallback(() => {
    if (!state.isExpiryFocused) {
      setState((prev) => ({ ...prev, expiry: '', isExpiryFocused: true, sessionTouched: true }));
    }
  }, [state.isExpiryFocused]);

  const getValue = debounce(handleCardNumberChange, 200, { leading: true });
  const getCcv = debounce(handleCcvChange, 200, { leading: true });


  // API call for new card payment
  const sendToApi = useCallback(
    async (data) => {
      const apiUrl = process.env.REACT_APP_PAYMENT_API_URL || 'https://baasapidev.lacaissepay.ma/api/operations/payment/link';
      
      if (!LACAISSE_TOKEN) {
        throw new Error('X-LACAISSE-TOKEN not configured');
      }

      // New card payment payload
      const payload = {
        phoneNumber: data.phoneNumber || "+212605032284",
        firstName: data.firstName,
        lastName: data.lastName,
        pan: data.pan,
        expiryDate: data.expiryDate,
        cvv: data.cvv,
        amount: data.amount,
        keepAlive: data.keepAlive || false,
        acceptUrl: data.acceptUrl || "https://macaisse.ma",
        declineUrl: data.declineUrl || "https://macaisse.ma",
        notificationUrl: data.notificationUrl || "https://macaisse.ma",
        externalReference: data.externalReference || ""
      };

      // Add cardName if provided (only when keepAlive is true)
      if (data.cardName) {
        payload.cardName = data.cardName;
        customLog('✅ cardName added to API payload:', data.cardName);
      }

      customLog('Sending new card payment request:', payload);

      try {
        const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            'X-LACAISSE-TOKEN': LACAISSE_TOKEN,
            },
          body: JSON.stringify(payload),
          });

          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`HTTP error! Status: ${response.status} - ${errorText}`);
          }

          const responseData = await response.json();
        customLog('Payment response:', responseData);
        
        // Check if the response indicates success
        if (responseData.status === 'success' && responseData.data?.data?.redirect) {
          const redirectionURL = responseData.data.data.redirectionURL;
          if (redirectionURL) {
            customLog('Redirecting to:', redirectionURL);
            window.location.href = redirectionURL;
            return responseData;
          }
        }

          return responseData;
        } catch (error) {
        customError('Payment API error:', error.message);
          throw error;
        }
    },
    []
  );

  // Initialization
  useEffect(() => {
    const initializeApp = async () => {
      if (hasInitialized.current) return;
      hasInitialized.current = true;

      if (!LACAISSE_TOKEN) {
        customError('REACT_APP_LACAISSE_TOKEN is not set.');
      }

      setState((prev) => ({ ...prev, loading: true }));

      const urlParams = new URLSearchParams(window.location.search);
      const sessionId = urlParams.get('sessionId');
      const configBase64 = urlParams.get('config');
      const emailFromUrl = urlParams.get('email');

      if (!sessionId || !configBase64) {
        customError('Missing sessionId or config in URL');
        setState((prev) => ({
          ...prev,
          errormsg: 'Missing session or configuration',
          isAppReady: false,
          loading: false,
        }));
        return;
      }

      let config, email;
      try {
        config = JSON.parse(atob(configBase64));
        email = emailFromUrl || config?.customer?.email;
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          customError('Invalid email format:', email);
          setState((prev) => ({
            ...prev,
            errormsg: 'Invalid email format provided',
            isAppReady: false,
            loading: false,
          }));
          return;
        }
      } catch (error) {
        customError('Failed to parse config:', error.message);
        setState((prev) => ({
          ...prev,
          errormsg: 'Invalid configuration',
          isAppReady: false,
          loading: false,
        }));
        return;
      }

      const sessionKey = `session_${sessionId}`;
      const sessionTouched = localStorage.getItem(sessionKey);

      if (sessionTouched) {
        customError('Session already touched, blocking app');
        setState((prev) => ({
          ...prev,
          errormsg: 'Application not working',
          isAppReady: false,
          sessionId,
          config: null,
          loading: false,
        }));
        return;
      }

      try {
        customLog(`Initializing with sessionId: ${sessionId}, email: ${email}, config:`, config);
        setState((prev) => ({ ...prev, sessionId, config, email }));

        const sessionToken = await verifySession(sessionId);
        if (!sessionToken) return;

        setState((prev) => ({ ...prev, sessionToken, sessionVerified: true }));

        const data = await fetchPaymentData(sessionId, sessionToken);

        // Fetch tokenized cards
        const phoneNumber = config.customer?.phone;
        const allCards = await fetchTokenizedCards(phoneNumber);
        
        // Filter cards to only show those belonging to the current phoneClient
        const phoneClient = config.customer?.phoneClient;
        const filteredCards = allCards.filter(card => {
          if (!phoneClient || !card.cardName) return false;
          
          const cleanPhoneClient = phoneClient.replace(/[^\d]/g, ''); // Remove all non-digits
          const cleanCardName = card.cardName.replace(/[^\d]/g, ''); // Remove all non-digits
          
          const isMatch = cleanCardName === cleanPhoneClient || 
                         card.cardName.toLowerCase().includes(phoneClient.toLowerCase()) ||
                         cleanCardName.includes(cleanPhoneClient);
          
          customLog('Card filtering check:', {
            cardName: card.cardName,
            cleanCardName: cleanCardName,
            phoneClient: phoneClient,
            cleanPhoneClient: cleanPhoneClient,
            isMatch: isMatch,
            customerBankCardId: card.customerBankCardId
          });
          
          return isMatch;
        });
        
        customLog('Cards filtering result:', {
          totalCards: allCards.length,
          filteredCards: filteredCards.length,
          phoneClient: phoneClient,
          originalCards: allCards.map(c => ({ cardName: c.cardName, customerBankCardId: c.customerBankCardId })),
          filteredCards: filteredCards.map(c => ({ cardName: c.cardName, customerBankCardId: c.customerBankCardId }))
        });

        setState((prev) => ({
          ...prev,
          amount: data.amount,
          orderId: data.orderId,
          savedCards: filteredCards,
          isAppReady: true,
          loading: false,
        }));

        localStorage.setItem(sessionKey, 'true');
        customLog(`Marking session ${sessionId} as touched`);
      } catch (error) {
        customError('Initialization error:', error.message);
        setState((prev) => ({
          ...prev,
          errormsg: error.message.split(' - ')[0],
          isAppReady: false,
          loading: false,
        }));
      }
    };

    initializeApp();
  }, [verifySession, fetchPaymentData, fetchTokenizedCards]);

  // Prefill name input with firstName and lastName from config
  useEffect(() => {
    if (state.config?.customer) {
      const firstName = state.config.customer.firstName || state.config.customer.prenomclient || '';
      const lastName = state.config.customer.lastName || state.config.customer.nomclient || '';
      
      if (firstName || lastName) {
        const fullName = `${firstName} ${lastName}`.trim();
        setState((prev) => ({
          ...prev,
          name: fullName,
        }));
      }
    }
  }, [state.config]);

  // Submit Handler
  const handleSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      setState((prev) => ({ ...prev, loading: true, sessionTouched: true }));

      if (!state.sessionVerified || !state.isAppReady || !state.config) {
        setState((prev) => ({
          ...prev,
          errormsg: getFrenchErrorMessage('Session not verified'),
          loading: false,
          showErrorModal: true,
        }));
        return;
      }

      const now = Date.now();
      if (state.submitAttempts >= MAX_SUBMIT_ATTEMPTS && now - state.lastSubmitTime < SUBMIT_COOLDOWN_MS) {
        setState((prev) => ({
          ...prev,
          errormsg: getFrenchErrorMessage('Too many attempts'),
          loading: false,
          showErrorModal: true,
        }));
        return;
      }

      const { name, input, expiry, ccv, amount, config, sessionId, sessionToken } = state;
      
      // Check for missing fields and build detailed error message
      const missingFields = [];
      if (!name || !name.trim()) missingFields.push('Nom du titulaire');
      if (!input || input.trim().length === 0) missingFields.push('Numéro de carte');
      if (!expiry || expiry.trim().length === 0) missingFields.push('Date d\'expiration');
      if (!ccv || ccv.trim().length === 0) missingFields.push('CVV');
      
      if (missingFields.length > 0) {
        const fieldsList = missingFields.join(', ');
        const errorMsg = missingFields.length === 1 
          ? `Le champ suivant est obligatoire : ${fieldsList}`
          : `Les champs suivants sont obligatoires : ${fieldsList}`;
        setState((prev) => ({
          ...prev,
          errormsg: errorMsg,
          loading: false,
          showErrorModal: true,
        }));
        return;
      }

      const rawInput = input.replace(/\s/g, '');
      const cardValidation = validateCardNumber(rawInput);
      if (!cardValidation.valid) {
        setState((prev) => ({
          ...prev,
          errormsg: getFrenchErrorMessage(cardValidation.error),
          loading: false,
          showErrorModal: true,
        }));
        return;
      }

      const [month, year] = expiry.split('/');
      const fullYear = parseInt(`20${year || '00'}`, 10);
      const monthNum = parseInt(month, 10);
      const currentYear = new Date().getFullYear();
      const currentMonth = new Date().getMonth() + 1;

      if (isNaN(fullYear) || isNaN(monthNum) || monthNum < 1 || monthNum > 12) {
        setState((prev) => ({
          ...prev,
          errormsg: getFrenchErrorMessage('Invalid expiration date'),
          loading: false,
          showErrorModal: true,
        }));
        return;
      }

      if (fullYear < currentYear || (fullYear === currentYear && monthNum < currentMonth)) {
        setState((prev) => ({
          ...prev,
          errormsg: getFrenchErrorMessage('Expiration date has passed'),
          loading: false,
          showErrorModal: true,
        }));
        return;
      }

      try {
        const formattedExpiry = `${year.padStart(2, '0')}${monthNum.toString().padStart(2, '0')}`;
        const firstName = name.split(' ')[0] || 'Unknown';
        const lastName = name.split(' ').slice(1).join(' ') || 'Unknown';

        // Clean phone number to ensure proper format
        let phoneNumber = config.customer?.phone;
        const originalPhone = phoneNumber;
        
        // Remove all spaces and special characters
        phoneNumber = phoneNumber.replace(/[^\d+]/g, '');
        
        // Comprehensive phone number cleaning
        if (phoneNumber.startsWith('+212')) {
          // Extract the part after +212
          let numberPart = phoneNumber.substring(4);
          
          // Remove any leading zeros
          numberPart = numberPart.replace(/^0+/, '');
          
          // Remove any duplicate "212" at the beginning of the number part
          // This handles cases like: 212611159538, 21212605032284, etc.
          numberPart = numberPart.replace(/^212+/, '');
          
          // Reconstruct the phone number
          phoneNumber = '+212' + numberPart;
        }
        
        customLog(`Phone number cleaning: ${originalPhone} -> ${phoneNumber}`);

        const paymentPayload = {
          phoneNumber: phoneNumber,
          firstName: firstName,
          lastName: lastName,
          pan: rawInput,
          expiryDate: formattedExpiry,
          cvv: ccv,
          amount: parseFloat(amount),
          keepAlive: state.saveCard || false,
          acceptUrl: config.urls?.accept || "https://macaisse.ma",
          declineUrl: config.urls?.decline || "https://macaisse.ma",
          notificationUrl: config.urls?.notification || "https://macaisse.ma",
          externalReference: config.urls?.externalReference || ""
        };

        // Add cardName only if saveCard is checked
        if (state.saveCard) {
          const phoneClient = config.customer?.phoneClient;
          if (phoneClient) {
            paymentPayload.cardName = phoneClient;
            customLog('✅ Adding cardName to payment:', phoneClient);
          } else {
            customLog('⚠️ saveCard checked but no phoneClient/phone found in config');
          }
        } else {
          customLog('ℹ️ saveCard not checked, cardName not added');
        }

        customLog('Sending payment request:', paymentPayload);
        const paymentResponse = await sendToApi(paymentPayload);

        // Save card if checkbox was checked
        if (state.saveCard) {
          try {
            // Format card number: show first 6 and last 4 digits (e.g., 423067***3851)
            const maskedCardNumber = rawInput.length >= 10 
              ? `${rawInput.slice(0, 6)}***${rawInput.slice(-4)}`
              : rawInput;
            
            const phoneClient = config.customer?.phoneClient || config.customer?.phone || '';
            const nomClient = `${config.customer?.firstName || ''} ${config.customer?.lastName || ''}`.trim();

            // Check if user_id exists
            if (config.customer?.user_id) {
              // User has user_id - call /saveCard
              customLog('User has user_id, calling /saveCard');
              const saveCardUrl = process.env.REACT_APP_SAVE_CARD_API_URL || 'https://sessiondev.lacaissepay.ma/saveCard';
              // Prod URL: const saveCardUrl = 'https://sessiondev.lacaissepay.ma/saveCard';
              
              const saveResponse = await fetch(saveCardUrl, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  number_card: maskedCardNumber,
                  token: config.customer.user_id, // uuiduser as token
                  uuiduser: config.customer.user_id,
                  nom_client: nomClient,
                  label: phoneClient,
                  expiry_date: formattedExpiry,
                }),
              });
              
              const saveResult = await saveResponse.json();
              customLog('Card saved successfully via /saveCard:', saveResult);
            } else {
              // User doesn't have user_id - call /saveCardLacaisse
              customLog('User does not have user_id, calling /saveCardLacaisse');
              const saveCardLacaisseUrl = process.env.REACT_APP_SAVE_CARD_LACAISSE_API_URL || 'https://sessiondev.lacaissepay.ma/saveCardLacaisse';
              // Prod URL: const saveCardLacaisseUrl = 'https://sessiondev.lacaissepay.ma/saveCardLacaisse';
              
              const commercant = config.customer?.phone;
              
              const saveResponse = await fetch(saveCardLacaisseUrl, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  number_card: maskedCardNumber,
                  nom_client: nomClient,
                  label: phoneClient, // label is phone_client
                  commercant: commercant,
                  expiry_date: formattedExpiry,
                }),
              });
              
              const saveResult = await saveResponse.json();
              customLog('Card saved successfully via /saveCardLacaisse:', saveResult);
            }
          } catch (saveError) {
            customError('Failed to save card:', saveError.message);
            // Don't block payment flow if card save fails
          }
        }

        // Handle session cleanup if needed
        if (config.singleUse) {
          try {
            await fetch('https://sessiondev.lacaissepay.ma/markSessionUsed', {
              // Prod URL: await fetch('https://sessiondev.lacaissepay.ma/markSessionUsed', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
                'X-Session-Token': sessionToken,
              },
              body: JSON.stringify({ sessionId }),
            });
            customLog('Session marked as used');
            const sessionKey = `session_${sessionId}`;
            localStorage.removeItem(sessionKey);
          } catch (markError) {
            customError('Failed to mark session as used:', markError);
          }
        }
      } catch (error) {
        customError('Payment error:', error.message);
        setState((prev) => ({
          ...prev,
          errormsg: getFrenchErrorMessage(error.message),
          loading: false,
          showErrorModal: true,
        }));
      } finally {
        setState((prev) => ({
          ...prev,
          submitAttempts: prev.submitAttempts + 1,
          lastSubmitTime: now,
          loading: false,
        }));
      }
    },
    [state, validateCardNumber, sendToApi]
  );

  // Automatic redirect to decline URL on error/blocked state
  useEffect(() => {
    if (!state.loading && (!state.isAppReady || !state.sessionVerified)) {
      const declineUrl = state.config?.urls?.decline || 'https://channelmanager.ma/paiement/rejected.html';
      const timeout = setTimeout(() => {
        window.location.href = declineUrl;
      }, 2000); // 2 seconds delay
      return () => clearTimeout(timeout);
    }
  }, [state.isAppReady, state.sessionVerified, state.loading, state.config]);

  // Close error modal handler
  const closeErrorModal = useCallback(() => {
    setState((prev) => ({ ...prev, showErrorModal: false, errormsg: '' }));
  }, []);

  // Handle saved card selection
  const handleCardSelection = useCallback((card) => {
    setState((prev) => ({
      ...prev,
      selectedCard: card,
      showCvvModal: true,
      savedCardCvv: '',
    }));
  }, []);

  // Handle CVV input for saved card
  const handleSavedCardCvvChange = useCallback((value) => {
    const sanitizedValue = value.replace(/\D/g, '').slice(0, 4);
    setState((prev) => ({ ...prev, savedCardCvv: sanitizedValue }));
  }, []);

  // Handle "Pay with new card" click
  const handleUseNewCard = useCallback(() => {
    setState((prev) => ({
      ...prev,
      useNewCard: true,
      selectedCard: null,
      showCvvModal: false,
      savedCardCvv: '',
    }));
  }, []);

  // Handle "Return to saved cards" click
  const handleReturnToSavedCards = useCallback(() => {
    setState((prev) => ({
      ...prev,
      useNewCard: false,
      selectedCard: null,
      showCvvModal: false,
      savedCardCvv: '',
    }));
  }, []);

  // Handle delete saved card - show confirmation modal
  const handleDeleteCard = useCallback((card, event) => {
    event.stopPropagation(); // Prevent card selection when clicking delete
    setState((prev) => ({
      ...prev,
      showDeleteModal: true,
      cardToDelete: card,
    }));
  }, []);

  // Confirm delete card
  const confirmDeleteCard = useCallback(async () => {
    if (!state.cardToDelete) return;

    try {
      const phoneNumber = state.config?.customer?.phone;
      if (!phoneNumber) {
        throw new Error('Phone number not found');
      }

      const apiUrl = `https://baasapidev.lacaissepay.ma/api/customers/tokenized/cards/${state.cardToDelete.customerBankCardId}?phoneNumber=${encodeURIComponent(phoneNumber)}`;
      const lacaisseToken = '53b3329da8c333a0c0aaaa5ebd2b9776cfc87226800cfcbc109aac4d4a380b06';

      customLog('Deleting card:', state.cardToDelete.customerBankCardId);

      const response = await fetch(apiUrl, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'x-lacaisse-token': lacaisseToken,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to delete card: ${response.status}`);
      }

      customLog('Card deleted successfully');

      // Remove the card from the saved cards list
      setState((prev) => ({
        ...prev,
        savedCards: prev.savedCards.filter(c => c.customerBankCardId !== state.cardToDelete.customerBankCardId),
        showDeleteModal: false,
        cardToDelete: null,
      }));

    } catch (error) {
      customError('Error deleting card:', error.message);
      setState((prev) => ({
        ...prev,
        errormsg: 'Erreur lors de la suppression de la carte',
        showErrorModal: true,
        showDeleteModal: false,
        cardToDelete: null,
      }));
    }
  }, [state.cardToDelete, state.config]);

  // Cancel delete card
  const cancelDeleteCard = useCallback(() => {
    setState((prev) => ({
      ...prev,
      showDeleteModal: false,
      cardToDelete: null,
    }));
  }, []);

  // Handle submit with saved card
  const handleSubmitSavedCard = useCallback(async () => {
    if (!state.savedCardCvv || state.savedCardCvv.length < 3) {
      setState((prev) => ({
        ...prev,
        errormsg: 'Veuillez entrer un code CVV valide',
        showErrorModal: true,
      }));
      return;
    }

    setState((prev) => ({ ...prev, loading: true }));

    try {
      const { selectedCard, config, amount } = state;
      
      // Clean phone number
      let phoneNumber = config.customer?.phone;
      phoneNumber = phoneNumber.replace(/[^\d+]/g, '');
      if (phoneNumber.startsWith('+212')) {
        let numberPart = phoneNumber.substring(4);
        numberPart = numberPart.replace(/^0+/, '');
        numberPart = numberPart.replace(/^212+/, '');
        phoneNumber = '+212' + numberPart;
      }

      // Call tokenized card payment endpoint
      const apiUrl = `https://baasapidev.lacaissepay.ma/api/operations/payment/link/tokenized/card/${selectedCard.customerBankCardId}?phoneNumber=${encodeURIComponent(phoneNumber)}`;
      const lacaisseToken = '53b3329da8c333a0c0aaaa5ebd2b9776cfc87226800cfcbc109aac4d4a380b06';

      const paymentPayload = {
        cvv: state.savedCardCvv,
        amount: parseFloat(amount),
        acceptUrl: config.urls?.accept || "https://macaisse.ma",
        declineUrl: config.urls?.decline || "https://macaisse.ma",
        notificationUrl: config.urls?.notification || "https://macaisse.ma",
        externalReference: config.urls?.externalReference || ""
      };

      customLog('Sending payment with saved card:', {
        customerBankCardId: selectedCard.customerBankCardId,
        phoneNumber: phoneNumber,
        payload: paymentPayload
      });

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-lacaisse-token': lacaisseToken,
        },
        body: JSON.stringify(paymentPayload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! Status: ${response.status} - ${errorText}`);
      }

      const responseData = await response.json();
      customLog('Tokenized card payment response:', responseData);

      // Check if the response indicates success and redirect
      if (responseData.status === 'success' && responseData.data?.data?.redirectionURL) {
        const redirectionURL = responseData.data.data.redirectionURL;
        if (redirectionURL) {
          customLog('Redirecting to:', redirectionURL);
          window.location.href = redirectionURL;
        }
      }

      // Handle session cleanup if needed
      if (config.singleUse) {
        try {
          await fetch('https://sessiondev.lacaissepay.ma/markSessionUsed', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
              'X-Session-Token': state.sessionToken,
            },
            body: JSON.stringify({ sessionId: state.sessionId }),
          });
          customLog('Session marked as used');
          const sessionKey = `session_${state.sessionId}`;
          localStorage.removeItem(sessionKey);
        } catch (markError) {
          customError('Failed to mark session as used:', markError);
        }
      }
    } catch (error) {
      customError('Payment error:', error.message);
      setState((prev) => ({
        ...prev,
        errormsg: getFrenchErrorMessage(error.message),
        loading: false,
        showErrorModal: true,
        showCvvModal: false,
      }));
    }
  }, [state]);

  return (
    <div style={{ position: 'relative', minHeight: '100vh', minWidth: '100vw', overflow: 'hidden' }}>
      {/* Blurred background image */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: "url('https://lalivraison.ma/assets/img/background-main.jpg') no-repeat center center fixed",
          backgroundSize: 'cover',
          filter: 'blur(8px)',
          zIndex: 0,
          pointerEvents: 'none',
          opacity: 1,
        }}
        aria-hidden="true"
      />
      {/* White overlay with lower opacity */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(255, 255, 255, 0.3)',
          zIndex: 1,
          pointerEvents: 'none',
        }}
        aria-hidden="true"
      />
      {/* Error Modal */}
      {state.showErrorModal && state.errormsg && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: window.innerWidth <= 768 ? '16px' : '20px',
            boxSizing: 'border-box',
          }}
          onClick={closeErrorModal}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '16px',
              padding: window.innerWidth <= 768 ? '24px' : '30px',
              maxWidth: '450px',
              width: '100%',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              position: 'relative',
              fontFamily: 'Inter, sans-serif',
              boxSizing: 'border-box',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Icon */}
            <div
              style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                backgroundColor: '#fee2e2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
              }}
            >
              <svg
                width="30"
                height="30"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 8V12M12 16H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                  stroke="#dc2626"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            {/* Title */}
            <h2
              style={{
                fontSize: '1.5rem',
                fontWeight: '600',
                color: '#1a1a1a',
                textAlign: 'center',
                marginBottom: '15px',
              }}
            >
              Erreur de paiement
            </h2>
            {/* Message */}
            <p
              style={{
                fontSize: '1rem',
                color: '#4a5568',
                textAlign: 'center',
                lineHeight: '1.6',
                marginBottom: '25px',
              }}
            >
              {state.errormsg}
            </p>
            {/* Button */}
            <button
              onClick={closeErrorModal}
              style={{
                width: '100%',
                padding: '16px',
                background: 'linear-gradient(135deg, #b4243a 0%, #8b0000 100%)',
                color: '#ffffff',
                border: 'none',
                borderRadius: '14px',
                fontSize: '1rem',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                boxShadow: '0 6px 20px rgba(180, 36, 58, 0.4)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'linear-gradient(135deg, #9a1e32 0%, #6b0000 100%)';
                e.target.style.transform = 'translateY(-2px) scale(1.02)';
                e.target.style.boxShadow = '0 10px 30px rgba(180, 36, 58, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'linear-gradient(135deg, #b4243a 0%, #8b0000 100%)';
                e.target.style.transform = 'translateY(0) scale(1)';
                e.target.style.boxShadow = '0 6px 20px rgba(180, 36, 58, 0.4)';
              }}
              onMouseDown={(e) => {
                e.target.style.transform = 'translateY(0) scale(0.98)';
              }}
              onMouseUp={(e) => {
                e.target.style.transform = 'translateY(-2px) scale(1.02)';
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M5 13l4 4L19 7"></path>
              </svg>
              <span>Compris</span>
            </button>
          </div>
        </div>
      )}
      {/* CVV Modal for Saved Card */}
      {state.showCvvModal && state.selectedCard && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: window.innerWidth <= 768 ? '16px' : '20px',
            animation: 'fadeIn 0.2s ease-out',
            boxSizing: 'border-box',
          }}
          onClick={() => setState((prev) => ({ ...prev, showCvvModal: false, savedCardCvv: '' }))}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '20px',
              padding: window.innerWidth <= 768 ? '24px' : '36px',
              maxWidth: '420px',
              width: '100%',
              boxShadow: '0 24px 48px rgba(0, 0, 0, 0.2), 0 12px 24px rgba(0, 0, 0, 0.12)',
              position: 'relative',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              animation: 'slideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              boxSizing: 'border-box',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Card Icon */}
            <div
              style={{
                width: '72px',
                height: '72px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, rgba(180, 36, 58, 0.1) 0%, rgba(180, 36, 58, 0.05) 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px',
                border: '2px solid rgba(180, 36, 58, 0.1)',
              }}
            >
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                  stroke="#b4243a"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            {/* Title */}
            <h2
              style={{
                fontSize: window.innerWidth <= 768 ? '1.4rem' : '1.6rem',
                fontWeight: '700',
                color: '#1a1a1a',
                textAlign: 'center',
                marginBottom: '12px',
                letterSpacing: '-0.02em',
              }}
            >
              Entrez votre CVV
            </h2>
            {/* Card Info */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                fontSize: '0.95rem',
                color: '#6b7280',
                marginBottom: '16px',
                padding: '12px',
                backgroundColor: '#f9fafb',
                borderRadius: '10px',
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                <line x1="1" y1="10" x2="23" y2="10"></line>
              </svg>
              <span style={{ fontWeight: '500', fontFamily: 'monospace', letterSpacing: '0.05em' }}>
                •••• {state.selectedCard.maskedPan}
              </span>
            </div>
            {/* Amount Display */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '28px',
                padding: '16px',
                background: 'linear-gradient(135deg, rgba(180, 36, 58, 0.08) 0%, rgba(180, 36, 58, 0.04) 100%)',
                borderRadius: '12px',
                border: '2px solid rgba(180, 36, 58, 0.15)',
              }}
            >
              <span style={{ 
                fontSize: '0.85rem', 
                color: '#6b7280', 
                fontWeight: '600',
                marginBottom: '6px',
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
              }}>
                Montant à payer
              </span>
              <span style={{ 
                fontSize: window.innerWidth <= 768 ? '1.8rem' : '2rem', 
                color: '#b4243a', 
                fontWeight: '800',
                letterSpacing: '-0.02em',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              }}>
                {parseFloat(state.amount).toFixed(2)} MAD
              </span>
            </div>
            {/* CVV Input */}
            <input
              type="text"
              inputMode="numeric"
              maxLength="4"
              placeholder="• • •"
              value={state.savedCardCvv}
              onChange={(e) => handleSavedCardCvvChange(e.target.value)}
              autoFocus
              style={{
                width: '100%',
                padding: '18px',
                fontSize: '1.3rem',
                border: '2px solid #e5e7eb',
                borderRadius: '12px',
                textAlign: 'center',
                marginBottom: '24px',
                fontFamily: 'SF Mono, Monaco, "Cascadia Code", "Roboto Mono", Consolas, "Courier New", monospace',
                letterSpacing: '0.5em',
                transition: 'all 0.2s ease',
                outline: 'none',
                boxSizing: 'border-box',
                backgroundColor: '#fafafa',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#b4243a';
                e.target.style.backgroundColor = '#ffffff';
                e.target.style.boxShadow = '0 0 0 4px rgba(180, 36, 58, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e5e7eb';
                e.target.style.backgroundColor = '#fafafa';
                e.target.style.boxShadow = 'none';
              }}
            />
            {/* Buttons */}
            <div style={{ display: 'flex', gap: '12px', flexDirection: window.innerWidth <= 480 ? 'column' : 'row' }}>
              <button
                onClick={() => setState((prev) => ({ ...prev, showCvvModal: false, savedCardCvv: '' }))}
                style={{
                  flex: 1,
                  padding: '16px',
                  background: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
                  color: '#374151',
                  border: '2px solid #e5e7eb',
                  borderRadius: '14px',
                  fontSize: '1rem',
                  fontWeight: '700',
                cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                  boxShadow: '0 2px 6px rgba(0, 0, 0, 0.05)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'linear-gradient(135deg, #e5e7eb 0%, #d1d5db 100%)';
                  e.target.style.borderColor = '#9ca3af';
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)';
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.05)';
                }}
                onMouseDown={(e) => {
                  e.target.style.transform = 'translateY(0) scale(0.98)';
                }}
                onMouseUp={(e) => {
                  e.target.style.transform = 'translateY(-2px) scale(1)';
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
                <span>Annuler</span>
              </button>
              <button
                onClick={handleSubmitSavedCard}
                disabled={state.loading || state.savedCardCvv.length < 3}
                style={{
                  flex: 1,
                  padding: '16px',
                  background: (state.savedCardCvv.length >= 3 && !state.loading)
                    ? 'linear-gradient(135deg, #b4243a 0%, #8b0000 100%)'
                    : 'linear-gradient(135deg, #d1d5db 0%, #9ca3af 100%)',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '14px',
                  fontSize: '1rem',
                  fontWeight: '700',
                  cursor: (state.savedCardCvv.length >= 3 && !state.loading) ? 'pointer' : 'not-allowed',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                  boxShadow: (state.savedCardCvv.length >= 3 && !state.loading) 
                    ? '0 6px 20px rgba(180, 36, 58, 0.4)' 
                    : '0 2px 6px rgba(0, 0, 0, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: state.loading ? '8px' : '6px',
                }}
                onMouseEnter={(e) => {
                  if (state.savedCardCvv.length >= 3 && !state.loading) {
                    e.target.style.background = 'linear-gradient(135deg, #9a1e32 0%, #6b0000 100%)';
                    e.target.style.transform = 'translateY(-2px) scale(1.02)';
                    e.target.style.boxShadow = '0 10px 30px rgba(180, 36, 58, 0.5)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (state.savedCardCvv.length >= 3 && !state.loading) {
                    e.target.style.background = 'linear-gradient(135deg, #b4243a 0%, #8b0000 100%)';
                    e.target.style.transform = 'translateY(0) scale(1)';
                    e.target.style.boxShadow = '0 6px 20px rgba(180, 36, 58, 0.4)';
                  }
                }}
                onMouseDown={(e) => {
                  if (state.savedCardCvv.length >= 3 && !state.loading) {
                    e.target.style.transform = 'translateY(0) scale(0.98)';
                  }
                }}
                onMouseUp={(e) => {
                  if (state.savedCardCvv.length >= 3 && !state.loading) {
                    e.target.style.transform = 'translateY(-2px) scale(1.02)';
                  }
                }}
              >
                {state.loading ? (
                  <>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid #ffffff',
                      borderTopColor: 'transparent',
                      borderRadius: '50%',
                      animation: 'spin 0.6s linear infinite',
                    }} />
                    <span>Traitement...</span>
                  </>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span>Payer</span>
                  </>
                )}
            </button>
            </div>
          </div>
        </div>
      )}
      {/* Delete Card Confirmation Modal */}
      {state.showDeleteModal && state.cardToDelete && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: window.innerWidth <= 768 ? '16px' : '20px',
            animation: 'fadeIn 0.2s ease-out',
            boxSizing: 'border-box',
          }}
          onClick={cancelDeleteCard}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '20px',
              padding: window.innerWidth <= 768 ? '24px' : '36px',
              maxWidth: '420px',
              width: '100%',
              boxShadow: '0 24px 48px rgba(0, 0, 0, 0.2), 0 12px 24px rgba(0, 0, 0, 0.12)',
              position: 'relative',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              animation: 'slideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              boxSizing: 'border-box',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Warning Icon */}
            <div
              style={{
                width: '72px',
                height: '72px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, rgba(220, 38, 38, 0.1) 0%, rgba(220, 38, 38, 0.05) 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px',
                border: '2px solid rgba(220, 38, 38, 0.2)',
              }}
            >
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  stroke="#dc2626"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            {/* Title */}
            <h2
              style={{
                fontSize: window.innerWidth <= 768 ? '1.4rem' : '1.6rem',
                fontWeight: '700',
                color: '#1a1a1a',
                textAlign: 'center',
                marginBottom: '12px',
                letterSpacing: '-0.02em',
              }}
            >
              Supprimer la carte ?
            </h2>
            {/* Card Info */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                fontSize: '0.95rem',
                color: '#6b7280',
                marginBottom: '12px',
                padding: '12px',
                backgroundColor: '#f9fafb',
                borderRadius: '10px',
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                <line x1="1" y1="10" x2="23" y2="10"></line>
              </svg>
              <span style={{ fontWeight: '500', fontFamily: 'monospace', letterSpacing: '0.05em' }}>
                •••• {state.cardToDelete.maskedPan}
              </span>
            </div>
            {/* Warning Message */}
            <p
              style={{
                fontSize: '0.95rem',
                color: '#6b7280',
                textAlign: 'center',
                marginBottom: '28px',
                lineHeight: '1.6',
              }}
            >
              Cette action est irréversible. La carte sera définitivement supprimée de vos cartes enregistrées.
            </p>
            {/* Buttons */}
            <div style={{ display: 'flex', gap: '12px', flexDirection: window.innerWidth <= 480 ? 'column' : 'row' }}>
              <button
                onClick={cancelDeleteCard}
                style={{
                  flex: 1,
                  padding: '16px',
                  background: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
                  color: '#374151',
                  border: '2px solid #e5e7eb',
                  borderRadius: '14px',
                  fontSize: '1rem',
                  fontWeight: '700',
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                  boxShadow: '0 2px 6px rgba(0, 0, 0, 0.05)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'linear-gradient(135deg, #e5e7eb 0%, #d1d5db 100%)';
                  e.target.style.borderColor = '#9ca3af';
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)';
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.05)';
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
                <span>Annuler</span>
              </button>
              <button
                onClick={confirmDeleteCard}
                style={{
                  flex: 1,
                  padding: '16px',
                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '14px',
                  fontSize: '1rem',
                  fontWeight: '700',
                  cursor: 'pointer',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                  boxShadow: '0 6px 20px rgba(220, 38, 38, 0.4)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)';
                  e.target.style.transform = 'translateY(-2px) scale(1.02)';
                  e.target.style.boxShadow = '0 10px 30px rgba(220, 38, 38, 0.5)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)';
                  e.target.style.transform = 'translateY(0) scale(1)';
                  e.target.style.boxShadow = '0 6px 20px rgba(220, 38, 38, 0.4)';
                }}
                onMouseDown={(e) => {
                  e.target.style.transform = 'translateY(0) scale(0.98)';
                }}
                onMouseUp={(e) => {
                  e.target.style.transform = 'translateY(-2px) scale(1.02)';
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <polyline points="3 6 5 6 21 6"></polyline>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                </svg>
                <span>Supprimer</span>
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Main app content */}
      <div className="App" style={{ position: 'relative', zIndex: 2 }}>
        <img
          className="logo"
          src={state.config?.frontend?.logo || 'https://lalivraisonv2-api.lalivraison.ma/google_rewards/LACAISSELOGOs.png'}
          alt={`${state.config?.frontend?.companyName || 'La Caisse'} Logo`}
          loading="lazy"
        />
        {state.isAppReady && state.sessionVerified ? (
          <>
            {/* Show saved cards if phoneClient exists in cardName and not using new card */}
            {(() => {
              const shouldShowSavedCards = state.savedCards.length > 0 && hasPhoneClientInCards() && !state.useNewCard;
              customLog('Conditional rendering check:', {
                savedCardsLength: state.savedCards.length,
                hasPhoneClientInCards: hasPhoneClientInCards(),
                useNewCard: state.useNewCard,
                shouldShowSavedCards: shouldShowSavedCards
              });
              return shouldShowSavedCards;
            })() ? (
              <div style={{ 
                maxWidth: '520px', 
                margin: '0 auto', 
                padding: window.innerWidth <= 768 ? '16px' : '24px',
                position: 'relative',
                zIndex: 2,
                width: '100%',
                boxSizing: 'border-box',
              }}>
                {/* Header with card count */}
                <div style={{
                  textAlign: 'center',
                  marginBottom: window.innerWidth <= 768 ? '20px' : '28px',
                }}>
                  <h2 style={{ 
                    fontSize: window.innerWidth <= 768 ? '1.35rem' : '1.75rem',
                    fontWeight: '700', 
                    color: '#1a1a1a', 
                    marginBottom: '8px',
                    letterSpacing: '-0.02em',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                  }}>
                    Vos cartes enregistrées
                  </h2>
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '6px 16px',
                    backgroundColor: 'rgba(180, 36, 58, 0.08)',
                    borderRadius: '20px',
                    border: '1px solid rgba(180, 36, 58, 0.15)',
                  }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#b4243a" strokeWidth="2">
                      <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                      <line x1="1" y1="10" x2="23" y2="10"></line>
                    </svg>
                    <span style={{
                      fontSize: '0.875rem',
                      fontWeight: '600',
                      color: '#b4243a',
                    }}>
                      {state.savedCards.length} {state.savedCards.length === 1 ? 'carte' : 'cartes'}
                    </span>
                  </div>
                </div>
                
                {/* Saved Cards Carousel */}
                <div 
                  style={{ 
                    marginBottom: window.innerWidth <= 768 ? '20px' : '24px',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                  }}
                >
                  {/* Left Arrow */}
                  {state.savedCards.length > 1 && (
                    <button
                      onClick={() => {
                        const container = document.getElementById('cards-carousel');
                        if (container) {
                          container.scrollBy({
                            left: -320,
                            behavior: 'smooth'
                          });
                        }
                      }}
                      style={{
                        position: 'absolute',
                        left: '-12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 10,
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #b4243a 0%, #8b0000 100%)',
                        border: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 4px 12px rgba(180, 36, 58, 0.3)',
                        transition: 'all 0.3s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
                        e.currentTarget.style.boxShadow = '0 6px 16px rgba(180, 36, 58, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(180, 36, 58, 0.3)';
                      }}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                        <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  )}

                  {/* Cards Container */}
                  <div
                    id="cards-carousel"
                    style={{
                      display: 'flex',
                      gap: '16px',
                      overflowX: 'auto',
                      overflowY: 'hidden',
                      scrollBehavior: 'smooth',
                      padding: '0 24px',
                      scrollbarWidth: 'none',
                      msOverflowStyle: 'none',
                      WebkitScrollbar: { display: 'none' },
                      flex: 1,
                      alignItems: 'center',
                      position: 'relative',
                      justifyContent: state.savedCards.length === 1 ? 'center' : 'flex-start',
                    }}
                    className="cards-carousel-container"
                  >
                  {state.savedCards.map((card, index) => (
                    <div
                      key={card.customerBankCardId}
                      onClick={() => handleCardSelection(card)}
                      style={{
                        background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
                        borderRadius: '16px',
                        padding: window.innerWidth <= 768 ? '20px' : '24px',
                        cursor: 'pointer',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.06)',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        border: '1px solid rgba(180, 36, 58, 0.1)',
                        position: 'relative',
                        overflow: 'hidden',
                        animation: `slideUp 0.4s cubic-bezier(0.4, 0, 0.2, 1) ${index * 0.1}s both`,
                        minWidth: window.innerWidth <= 768 ? (state.savedCards.length === 1 ? '320px' : '200px') : '335px',
                        maxWidth: window.innerWidth <= 768 ? (state.savedCards.length === 1 ? '320px' : '250px') : '335px',
                        flexShrink: 0,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
                        e.currentTarget.style.boxShadow = '0 12px 24px rgba(180, 36, 58, 0.15), 0 6px 12px rgba(0, 0, 0, 0.1)';
                        e.currentTarget.style.borderColor = '#b4243a';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0) scale(1)';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.06)';
                        e.currentTarget.style.borderColor = 'rgba(180, 36, 58, 0.1)';
                      }}
                    >
                      {/* Delete button - Clean positioning */}
                      <button
                        onClick={(e) => handleDeleteCard(card, e)}
                        style={{
                          position: 'absolute',
                          top: '18px',
                          right: '18px',
                          width: '36px',
                          height: '36px',
                          borderRadius: '10px',
                          background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(220, 38, 38, 0.25) 100%)',
                          border: '2px solid rgba(239, 68, 68, 0.4)',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          zIndex: 20,
                          boxShadow: '0 3px 10px rgba(220, 38, 38, 0.2), 0 1px 3px rgba(0, 0, 0, 0.1)',
                          backdropFilter: 'blur(10px)',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)';
                          e.currentTarget.style.borderColor = '#dc2626';
                          e.currentTarget.style.transform = 'scale(1.18) rotate(5deg)';
                          e.currentTarget.style.boxShadow = '0 8px 25px rgba(220, 38, 38, 0.45), 0 0 20px rgba(220, 38, 38, 0.2)';
                          e.currentTarget.style.animation = 'none';
                          const svg = e.currentTarget.querySelector('svg');
                          if (svg) {
                            svg.setAttribute('stroke', '#ffffff');
                            svg.style.transform = 'scale(1.1)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'linear-gradient(135deg, rgba(239, 68, 68, 0.12) 0%, rgba(220, 38, 38, 0.18) 100%)';
                          e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.35)';
                          e.currentTarget.style.transform = 'scale(1) rotate(0deg)';
                          e.currentTarget.style.boxShadow = '0 2px 8px rgba(220, 38, 38, 0.15), 0 0 0 0 rgba(220, 38, 38, 0.3)';
                          e.currentTarget.style.animation = 'subtlePulse 3s ease-in-out infinite';
                          const svg = e.currentTarget.querySelector('svg');
                          if (svg) {
                            svg.setAttribute('stroke', '#dc2626');
                            svg.style.transform = 'scale(1)';
                          }
                        }}
                        onMouseDown={(e) => {
                          e.currentTarget.style.transform = 'scale(1.08) rotate(5deg)';
                        }}
                        onMouseUp={(e) => {
                          e.currentTarget.style.transform = 'scale(1.18) rotate(5deg)';
                        }}
                        aria-label="Delete card"
                      >
                        <svg 
                          width="19" 
                          height="19" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="#dc2626" 
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          style={{
                            transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                            filter: 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))',
                          }}
                        >
                          <polyline points="3 6 5 6 21 6"></polyline>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                          <line x1="10" y1="11" x2="10" y2="17"></line>
                          <line x1="14" y1="11" x2="14" y2="17"></line>
                        </svg>
                      </button>

                      {/* Decorative gradient overlay */}
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        width: '120px',
                        height: '120px',
                        background: 'radial-gradient(circle, rgba(180, 36, 58, 0.08) 0%, transparent 70%)',
                        pointerEvents: 'none',
                      }} />
                      
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'flex-start',
                        gap: '16px',
                        position: 'relative',
                        paddingRight: '70px', // Space for delete button
                      }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          {/* Card Number */}
                          <div style={{ 
                            fontSize: window.innerWidth <= 768 ? '1.35rem' : '1.5rem',
                            fontWeight: '700', 
                            color: '#1a1a1a',
                            marginBottom: '12px',
                            fontFamily: 'SF Mono, Monaco, "Cascadia Code", "Roboto Mono", Consolas, "Courier New", monospace',
                            letterSpacing: '0.05em',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                          }}>
                            {/* Show logo for Visa/Mastercard, dots for others */}
                            {card.scheme === 'VISA' ? (
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '32px',
                                height: '20px',
                                backgroundColor: '#1a1f71',
                                borderRadius: '4px',
                                marginRight: '8px',
                              }}>
                                <span style={{
                                  color: 'white',
                                  fontSize: '0.7rem',
                                  fontWeight: '700',
                                  letterSpacing: '0.05em',
                                }}>VISA</span>
                              </div>
                            ) : card.scheme === 'MASTERCARD' ? (
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '32px',
                                height: '20px',
                                backgroundColor: '#eb001b',
                                borderRadius: '4px',
                                marginRight: '8px',
                                position: 'relative',
                                overflow: 'hidden',
                              }}>
                                <div style={{
                                  position: 'absolute',
                                  left: '4px',
                                  width: '12px',
                                  height: '12px',
                                  backgroundColor: '#f79e1b',
                                  borderRadius: '50%',
                                }}></div>
                                <div style={{
                                  position: 'absolute',
                                  right: '4px',
                                  width: '12px',
                                  height: '12px',
                                  backgroundColor: '#ff5f00',
                                  borderRadius: '50%',
                                }}></div>
                              </div>
                            ) : (
                              <span style={{ 
                                fontSize: '1.1rem',
                                color: '#9ca3af',
                                marginRight: '8px',
                              }}>••••</span>
                            )}
                            <span>{card.maskedPan}</span>
                          </div>
                          
                          {/* Cardholder Name */}
                          <div style={{ 
                            fontSize: window.innerWidth <= 768 ? '0.95rem' : '1rem',
                            color: '#4b5563', 
                            marginBottom: '6px',
                            fontWeight: '500',
                            textTransform: 'uppercase',
                            letterSpacing: '0.03em',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}>
                            {[card.cardholderFirstname, card.cardholderLastname]
                              .filter(name => name && name.trim() !== '' && name !== 'UNKNOWN' && name !== 'Unknown' && name !== 'unknown')
                              .join(' ')}
                          </div>
                          
                          {/* Expiry Date */}
                          <div style={{ 
                            fontSize: '0.875rem', 
                            color: '#6b7280',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                          }}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                              <line x1="16" y1="2" x2="16" y2="6"></line>
                              <line x1="8" y1="2" x2="8" y2="6"></line>
                              <line x1="3" y1="10" x2="21" y2="10"></line>
                            </svg>
                            <span>Expire {card.cardExpiryDate.slice(0, 2)}/{card.cardExpiryDate.slice(2, 4)}</span>
                          </div>
                        </div>
                        
                      </div>
                      
                      {/* Click indicator */}
                      <div style={{
                        marginTop: '16px',
                        paddingTop: '16px',
                        borderTop: '1px solid rgba(0, 0, 0, 0.06)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        fontSize: '0.875rem',
                        color: '#b4243a',
                        fontWeight: '600',
                      }}>
                        <span>Cliquez pour payer</span>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M5 12h14M12 5l7 7-7 7"/>
                        </svg>
                      </div>
                    </div>
                  ))}
                  </div>


                  {/* Right Arrow */}
                  {state.savedCards.length > 1 && (
                    <button
                      onClick={() => {
                        const container = document.getElementById('cards-carousel');
                        if (container) {
                          container.scrollBy({
                            left: 320,
                            behavior: 'smooth'
                          });
                        }
                      }}
                      style={{
                        position: 'absolute',
                        right: '-12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 10,
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #b4243a 0%, #8b0000 100%)',
                        border: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 4px 12px rgba(180, 36, 58, 0.3)',
                        transition: 'all 0.3s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)';
                        e.currentTarget.style.boxShadow = '0 6px 16px rgba(180, 36, 58, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(-50%) scale(1)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(180, 36, 58, 0.3)';
                      }}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                        <path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </button>
                  )}
                </div>

                {/* Scroll indicator - show when there are more than 3 cards */}
                {state.savedCards.length > 3 && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    marginBottom: '20px',
                    padding: '12px',
                    backgroundColor: 'rgba(180, 36, 58, 0.04)',
                    borderRadius: '12px',
                    border: '1px dashed rgba(180, 36, 58, 0.2)',
                    animation: 'fadeIn 1s ease-in-out',
                  }}>
                    <svg 
                      width="16" 
                      height="16" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="#b4243a" 
                      strokeWidth="2"
                      style={{ animation: 'bounceDown 1.5s ease-in-out infinite' }}
                    >
                      <path d="M12 5v14M19 12l-7 7-7-7"/>
                    </svg>
                    <span style={{
                      fontSize: '0.85rem',
                      color: '#6b7280',
                      fontWeight: '500',
                    }}>
                      {state.savedCards.length - 3} carte{state.savedCards.length - 3 > 1 ? 's' : ''} supplémentaire{state.savedCards.length - 3 > 1 ? 's' : ''} ci-dessous
                    </span>
                  </div>
                )}

                {/* Pay with new card button - only show if phoneClient exists in cards */}
                {hasPhoneClientInCards() && (
                  <button
                    onClick={handleUseNewCard}
                  style={{
                    width: '100%',
                    padding: '16px',
                    backgroundColor: '#ffffff',
                    color: '#b4243a',
                    border: '2px solid #b4243a',
                    borderRadius: '14px',
                    fontSize: window.innerWidth <= 768 ? '0.95rem' : '1rem',
                    fontWeight: '700',
                    cursor: 'pointer',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    marginBottom: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                    boxShadow: '0 2px 4px rgba(180, 36, 58, 0.1)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#b4243a';
                    e.currentTarget.style.color = '#ffffff';
                    e.currentTarget.style.transform = 'translateY(-2px) scale(1.02)';
                    e.currentTarget.style.boxShadow = '0 8px 16px rgba(180, 36, 58, 0.25)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#ffffff';
                    e.currentTarget.style.color = '#b4243a';
                    e.currentTarget.style.transform = 'translateY(0) scale(1)';
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(180, 36, 58, 0.1)';
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <line x1="12" y1="5" x2="12" y2="19"></line>
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                  </svg>
                  <span>Payer avec une nouvelle carte</span>
                </button>
                )}

                {/* Cancel button */}
                <div style={{ textAlign: 'center', marginTop: '12px' }}>
                  <button
                    onClick={() => (window.location.href = state.config?.urls?.decline)}
                    style={{
                      padding: window.innerWidth <= 768 ? '16px' : '18px',
                      background: 'linear-gradient(135deg, #8b0000 0%, #5a0000 100%)',
                      color: '#ffffff',
                      border: '2px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '14px',
                      fontSize: window.innerWidth <= 768 ? '0.95rem' : '1rem',
                      fontWeight: '700',
                      cursor: 'pointer',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      boxShadow: '0 4px 12px rgba(139, 0, 0, 0.3)',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      width: '100%',
                      maxWidth: '300px',
                      margin: '0 auto',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'linear-gradient(135deg, #700000 0%, #400000 100%)';
                      e.currentTarget.style.transform = 'translateY(-2px) scale(1.02)';
                      e.currentTarget.style.boxShadow = '0 8px 20px rgba(139, 0, 0, 0.4)';
                      e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'linear-gradient(135deg, #8b0000 0%, #5a0000 100%)';
                      e.currentTarget.style.transform = 'translateY(0) scale(1)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(139, 0, 0, 0.3)';
                      e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                    }}
                    onMouseDown={(e) => {
                      e.currentTarget.style.transform = 'translateY(0) scale(0.98)';
                    }}
                    onMouseUp={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px) scale(1.02)';
                    }}
                    aria-label="Cancel payment"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <line x1="18" y1="6" x2="6" y2="18"></line>
                      <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                    <span>Annuler</span>
                  </button>
                </div>
              </div>
            ) : (
          <>
            <CardDisplay
              cardType={state.cardType}
              input={state.input}
              name={state.name}
              expiry={state.expiry}
              ccv={state.ccv}
              theme={state.config?.frontend?.theme}
            />
            <div className="formwrapper">
                  {/* Return to saved cards button - show if phoneClient exists in cards */}
                  {state.savedCards.length > 0 && hasPhoneClientInCards() && (
                    <button
                      onClick={handleReturnToSavedCards}
                      style={{
                        width: '100%',
                        padding: '16px',
                        background: 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
                        color: '#374151',
                        border: '2px solid #e5e7eb',
                        borderRadius: '14px',
                        fontSize: '0.95rem',
                        fontWeight: '700',
                        cursor: 'pointer',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        marginBottom: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.05)',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'linear-gradient(135deg, #e5e7eb 0%, #d1d5db 100%)';
                        e.currentTarget.style.borderColor = '#b4243a';
                        e.currentTarget.style.color = '#b4243a';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)';
                        e.currentTarget.style.borderColor = '#e5e7eb';
                        e.currentTarget.style.color = '#374151';
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 2px 6px rgba(0, 0, 0, 0.05)';
                      }}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M19 12H5M12 19l-7-7 7-7"/>
                      </svg>
                      <span>Retour aux cartes enregistrées</span>
                    </button>
                  )}
                  
              <CardForm
                state={state}
                getValue={getValue}
                handleExpiryChange={handleExpiryChange}
                handleExpiryFocus={handleExpiryFocus}
                getCcv={getCcv}
                getName={getName}
                handleSubmit={handleSubmit}
                setState={setState}
                theme={state.config?.frontend?.theme}
              />
              <div style={{ textAlign: 'center', marginTop: '20px' }}>
                <button
                  onClick={() => (window.location.href = state.config?.urls?.decline)}
                  style={{
                    marginTop: '10px',
                        padding: '16px',
                        background: 'linear-gradient(135deg, #8b0000 0%, #5a0000 100%)',
                    color: 'white',
                        border: '2px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '14px',
                        fontSize: '1rem',
                        fontWeight: '700',
                    cursor: 'pointer',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        boxShadow: '0 4px 12px rgba(139, 0, 0, 0.3)',
                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        width: '100%',
                        maxWidth: '300px',
                        margin: '10px auto 0',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'linear-gradient(135deg, #700000 0%, #400000 100%)';
                        e.currentTarget.style.transform = 'translateY(-2px) scale(1.02)';
                        e.currentTarget.style.boxShadow = '0 8px 20px rgba(139, 0, 0, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'linear-gradient(135deg, #8b0000 0%, #5a0000 100%)';
                        e.currentTarget.style.transform = 'translateY(0) scale(1)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(139, 0, 0, 0.3)';
                  }}
                  aria-label="Cancel payment"
                >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                      <span>Annuler</span>
                </button>
              </div>
            </div>
              </>
            )}
          </>
        ) : (
          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            {state.loading ? (
              <div className="spinner" aria-label="Loading" />
            ) : (
              <p style={{ color: 'red' }} role="alert">
                {state.errormsg || 'Initializing...'}
              </p>
            )}
            <button
              onClick={() => (window.location.href = state.config?.urls?.decline)}
              style={{
                marginTop: '16px',
                padding: '16px',
                background: 'linear-gradient(135deg, #8b0000 0%, #5a0000 100%)',
                color: 'white',
                border: '2px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '14px',
                fontSize: '1rem',
                fontWeight: '700',
                cursor: 'pointer',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: '0 4px 12px rgba(139, 0, 0, 0.3)',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                maxWidth: '300px',
                margin: '16px auto 0',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, #700000 0%, #400000 100%)';
                e.currentTarget.style.transform = 'translateY(-2px) scale(1.02)';
                e.currentTarget.style.boxShadow = '0 8px 20px rgba(139, 0, 0, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, #8b0000 0%, #5a0000 100%)';
                e.currentTarget.style.transform = 'translateY(0) scale(1)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(139, 0, 0, 0.3)';
              }}
              aria-label="Cancel payment"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
              <span>Annuler</span>
            </button>
          </div>
        )}
        <footer>
          <p>
            Powered by{' '}
            <a
              style={{ color: '#b4243a', textDecoration: 'none', fontWeight: '600' }}
              href="https://lacaissepay.ma"
              target="_blank"
              rel="noopener noreferrer"
            >
              lacaissepay.ma
            </a>
          </p>
          <p>
            Sponsorisé par Chari Money établissement de paiement agrée par Bank Al Maghreb
          </p>
        </footer>
      </div>
    </div>
  );
};

export default App;